/**
 * Shared Orca runner protocol schemas.
 *
 * Serializable Effect `Schema` types shared by three peers:
 *
 *  1. the **orchestrator runtime** (`@fm/api`) — decides what containers to
 *     spawn/kill and issues {@link RunnerCommand}s,
 *  2. **`OrcaRunnerRpcs`** — the RPC group that carries those commands and the
 *     runner's replies, and
 *  3. the **daemon** — a separate process running on the Docker host that
 *     executes the commands and reports back.
 *
 * Because all three import these definitions, the module has no server-side
 * dependencies (only `effect`) so the daemon can pull it in cheaply.
 *
 * ## Secrets
 *
 * Every container is launched with both ordinary configuration and
 * secret-bearing environment variables (the Claude OAuth token, `GITHUB_TOKEN`,
 * the dotenvx private key, and the S3/AWS credentials used for archival).
 * Those secrets MUST never reach a log line or an archived task record. They
 * are therefore carried in a dedicated {@link ContainerSpec.secretEnv} map whose
 * values are {@link Schema.Redacted}: in memory they print as `<redacted>`
 * (so `JSON.stringify`, `console.log`, and Effect's logger all elide them), and
 * the real value is only recoverable via `Redacted.value` at the moment the
 * daemon shells out to `docker run`. The wire encoding still serializes the
 * underlying string — see "Transport" below for why that is safe.
 *
 * ## Transport
 *
 * `Schema.Redacted` protects against accidental logging/archival, not against
 * an eavesdropper: encoding a spec for transport emits the plaintext secret.
 * The runner channel that carries these payloads MUST therefore be **TLS-only**.
 * That is enforced by the `OrcaRunnerRpcs` transport layer; this module only
 * defines the payloads.
 *
 * @since 1.0.0
 */
import { Match, Redacted, Schema } from "effect";

// ============================================================================
// Secret env handling
// ============================================================================

/**
 * Environment-variable keys whose values are secret-bearing and must be carried
 * in {@link ContainerSpec.secretEnv} (redacted) rather than the plaintext
 * {@link ContainerSpec.env} map.
 *
 * Mirrors the secrets the in-process `AgentRunner` injects today: the Claude
 * OAuth token, the GitHub token, the dotenvx private key, the S3/AWS
 * credentials used by the archive flow, and the per-task terminal-bridge
 * HMAC sig (INF-727) the sidecar presents on its WebSocket handshake.
 *
 * @since 1.0.0
 */
export const SECRET_ENV_KEYS: ReadonlyArray<string> = [
  "CLAUDE_CODE_OAUTH_TOKEN",
  "CLAUDE_CODE_OAUTH_TOKENS",
  "CODEX_AUTH_JSON",
  "GITHUB_TOKEN",
  "DOTENV_PRIVATE_KEY",
  "SSH_PRIVATE_KEY",
  "AWS_ACCESS_KEY_ID",
  "AWS_SECRET_ACCESS_KEY",
  "ORCA_TERMINAL_SIG",
  // OL-33: the outcome-engine run's callback capability. Possession of it IS
  // the authorization for a run-report callback, so it must ride `secretEnv`
  // and never reach a log line or an archived spec.
  "ORCA_RUN_TOKEN",
  // OL-63-lease: the API-key credential kinds the daemon injects after
  // redeeming a lease ({@link credentialEnvForKind}). Appended — never
  // reordered — because sibling issues append here too. This list is a
  // REDACTION GUARD only: the api populates none of these three itself, but the
  // moment anything does, the value must ride `secretEnv`.
  "ANTHROPIC_API_KEY",
  "OPENAI_API_KEY",
  "LINEAR_API_KEY",
];

const SECRET_ENV_KEY_SET: ReadonlySet<string> = new Set(SECRET_ENV_KEYS);

/**
 * Whether an environment-variable key is secret-bearing and so must be routed
 * through {@link ContainerSpec.secretEnv}.
 *
 * @since 1.0.0
 */
export const isSecretEnvKey = (key: string): boolean => SECRET_ENV_KEY_SET.has(key);

/**
 * Split a flat environment map into its non-secret ({@link ContainerSpec.env})
 * and secret ({@link ContainerSpec.secretEnv}) halves, wrapping every secret
 * value in `Redacted` so it can never be logged or archived.
 *
 * Producers should always build a {@link ContainerSpec} through this helper
 * rather than populating the two maps by hand — that keeps the
 * "secrets are flagged" invariant in one place.
 *
 * @since 1.0.0
 */
export const partitionEnv = (
  env: Readonly<Record<string, string>>,
): {
  readonly env: Record<string, string>;
  readonly secretEnv: Record<string, Redacted.Redacted<string>>;
} => {
  const plain: Record<string, string> = {};
  const secret: Record<string, Redacted.Redacted<string>> = {};
  for (const [key, value] of Object.entries(env)) {
    if (isSecretEnvKey(key)) {
      secret[key] = Redacted.make(value);
    } else {
      plain[key] = value;
    }
  }
  return { env: plain, secretEnv: secret };
};

/**
 * Reconstitute the full, plaintext environment a container should launch with
 * by unwrapping every redacted secret. Intended for the daemon at `docker run`
 * time and nowhere else — the result holds raw secrets and must not be logged.
 *
 * On key collision the secret value wins, mirroring how a later `-e` would
 * override an earlier one.
 *
 * @since 1.0.0
 */
export const mergeEnvForSpawn = (
  spec: Pick<ContainerSpec, "env" | "secretEnv">,
): Record<string, string> => {
  const merged: Record<string, string> = { ...spec.env };
  for (const [key, value] of Object.entries(spec.secretEnv)) {
    merged[key] = Redacted.value(value);
  }
  return merged;
};

// ============================================================================
// ContainerSpec
// ============================================================================

/**
 * A bind mount or named-volume mount applied to a container (`docker run -v`).
 * `source` is a host path or named volume; `target` is the in-container path.
 *
 * @since 1.0.0
 */
export const ContainerMount = Schema.Struct({
  source: Schema.String,
  target: Schema.String,
  readonly: Schema.optionalWith(Schema.Boolean, { default: () => false }),
});

/**
 * @since 1.0.0
 */
export type ContainerMount = Schema.Schema.Type<typeof ContainerMount>;

/**
 * What kind of credential a {@link ContainerSpec.credentialRef} names.
 *
 * Declared HERE rather than imported from `@fm/db`'s `models/Credential.ts`
 * because this module deliberately imports `effect` and nothing else (see the
 * module docs) — the daemon pulls it in and has no `@fm/db` dependency. The two
 * literal sets are identical, and a unit test asserts they are equal so they
 * cannot drift.
 *
 * @since 1.0.0
 */
export const CredentialKind = Schema.Literal(
  "claude_oauth",
  "codex_oauth",
  "anthropic_api_key",
  "openai_api_key",
  "github_token",
  "linear_api_key",
  "aws_access_key",
);

/**
 * @since 1.0.0
 */
export type CredentialKind = Schema.Schema.Type<typeof CredentialKind>;

/**
 * A REFERENCE to a credential plus the one-time capability that releases it —
 * never the credential itself. The orchestrator leases a row from
 * `orcaloops.credentials` when it builds the spec and puts the row's
 * `{ id, kind }` here alongside a 120-second `leaseToken`; the daemon redeems
 * that token over the (TLS-only) runner channel immediately before `docker run`
 * and injects the plaintext by kind (OL-63-lease). Nothing is ever written to
 * the runner's disk.
 *
 * `leaseToken` is {@link Schema.Redacted} for exactly the reason `ORCA_RUN_TOKEN`
 * is (see {@link SECRET_ENV_KEYS}): possession of it IS the authorization to
 * redeem the credential, so it must never reach a log line or an archived spec.
 * The `id`/`kind` half is plain identity and carries no secret.
 *
 * @since 1.0.0
 */
export const CredentialRef = Schema.Struct({
  id: Schema.String,
  kind: CredentialKind,
  leaseToken: Schema.Redacted(Schema.String),
});

/**
 * @since 1.0.0
 */
export type CredentialRef = Schema.Schema.Type<typeof CredentialRef>;

/**
 * Translate one redeemed credential into the environment variables the worker
 * container expects for that {@link CredentialKind}.
 *
 * Declared HERE, beside the wire types, so the api (which decides what a kind
 * means) and the daemon (which does the injecting) share ONE implementation and
 * cannot drift.
 *
 * `values` is the decrypted field map the redeem RPC returns for a credential
 * row: `accessToken` for every single-valued kind, plus — for
 * `aws_access_key` — `secretAccessKey` and an optional `region`, which come
 * from the row's encrypted `secret_fields` JSON. An env name is emitted ONLY
 * when its source value is present, so a partially-populated row yields a
 * smaller map rather than an `undefined` that would spawn a container with
 * `-e AWS_DEFAULT_REGION=undefined`.
 *
 * `claude_oauth` fills BOTH Claude variables from the single leased value: the
 * container rotates within `CLAUDE_CODE_OAUTH_TOKENS` starting at
 * `ORCA_TOKEN_START_INDEX`, which the ref path pins to `"0"` because a lease
 * releases exactly one credential.
 *
 * @since 1.0.0
 */
export const credentialEnvForKind = (
  kind: CredentialKind,
  values: Readonly<Record<string, string>>,
): Record<string, string> => {
  const bind = (name: string, key: string): Record<string, string> => {
    const value = values[key];
    return value === undefined ? {} : { [name]: value };
  };
  return Match.value(kind).pipe(
    Match.when("claude_oauth", (): Record<string, string> => ({
      ...bind("CLAUDE_CODE_OAUTH_TOKEN", "accessToken"),
      ...bind("CLAUDE_CODE_OAUTH_TOKENS", "accessToken"),
    })),
    Match.when("codex_oauth", (): Record<string, string> => bind("CODEX_AUTH_JSON", "accessToken")),
    Match.when("anthropic_api_key", (): Record<string, string> => bind("ANTHROPIC_API_KEY", "accessToken")),
    Match.when("openai_api_key", (): Record<string, string> => bind("OPENAI_API_KEY", "accessToken")),
    Match.when("github_token", (): Record<string, string> => bind("GITHUB_TOKEN", "accessToken")),
    Match.when("linear_api_key", (): Record<string, string> => bind("LINEAR_API_KEY", "accessToken")),
    Match.when("aws_access_key", (): Record<string, string> => ({
      ...bind("AWS_ACCESS_KEY_ID", "accessToken"),
      ...bind("AWS_SECRET_ACCESS_KEY", "secretAccessKey"),
      ...bind("AWS_DEFAULT_REGION", "region"),
    })),
    Match.exhaustive,
  );
};

/**
 * Fully-resolved description of a container the daemon should launch.
 *
 * `image` is the repository (e.g. `fm-claude-worker`) and `tag` the tag
 * (e.g. `latest`); the daemon joins them as `image:tag`. Non-secret
 * environment lives in `env`; secret-bearing environment lives in `secretEnv`
 * as `Redacted` values that never log or archive — see the module docs.
 * `tmpfs` and `addHost` carry raw `docker run --tmpfs` / `--add-host` specs;
 * `memoryLimit`, when present, carries a raw `docker run --memory` size string
 * (e.g. `12g`) — absent means no memory limit, exactly the pre-field behavior.
 *
 * `credentialRef`, when present (OL-62, extended by OL-63-lease), names a
 * credential the daemon must REDEEM over the runner channel instead of reading
 * one out of `secretEnv` — the three credential env keys are then absent from
 * the spec entirely, and the plaintext exists on the runner only in the argv of
 * one `docker run`.
 * `Schema.optional` keeps every existing spec decoding unchanged, and
 * {@link ORCA_RUNNER_SPEC_VERSION} is deliberately NOT bumped (a bump makes
 * `register` reject every not-yet-upgraded daemon, taking the fleet down).
 * The consequence, stated plainly: a daemon that predates OL-63 decodes the
 * spec with excess properties ignored, so it drops the ref and spawns a
 * container with no credentials, which dies in `entrypoint.sh`. There is no
 * per-runner version gate, so `ORCALOOPS_CREDENTIAL_REFS_ENABLED` is the only
 * guard — it stays off until every runner reports the OL-63-lease build.
 *
 * @since 1.0.0
 */
export const ContainerSpec = Schema.Struct({
  image: Schema.String,
  tag: Schema.String,
  name: Schema.String,
  env: Schema.Record({ key: Schema.String, value: Schema.String }),
  secretEnv: Schema.Record({ key: Schema.String, value: Schema.Redacted(Schema.String) }),
  mounts: Schema.Array(ContainerMount),
  tmpfs: Schema.Array(Schema.String),
  addHost: Schema.Array(Schema.String),
  labels: Schema.Record({ key: Schema.String, value: Schema.String }),
  memoryLimit: Schema.optional(Schema.String),
  credentialRef: Schema.optional(CredentialRef),
});

/**
 * @since 1.0.0
 */
export type ContainerSpec = Schema.Schema.Type<typeof ContainerSpec>;

// ============================================================================
// RunnerCommand
// ============================================================================

/**
 * Command to launch a new container from a {@link ContainerSpec}. `commandId`
 * is a ULID the orchestrator generates so the daemon's {@link CommandAck} can
 * be correlated back to the request.
 *
 * @since 1.0.0
 */
export const SpawnContainer = Schema.TaggedStruct("SpawnContainer", {
  commandId: Schema.String,
  spec: ContainerSpec,
});

/**
 * @since 1.0.0
 */
export type SpawnContainer = Schema.Schema.Type<typeof SpawnContainer>;

/**
 * Command to stop and remove a container by its `name`. `commandId` correlates
 * the daemon's {@link CommandAck} back to this request.
 *
 * @since 1.0.0
 */
export const KillContainer = Schema.TaggedStruct("KillContainer", {
  commandId: Schema.String,
  name: Schema.String,
});

/**
 * @since 1.0.0
 */
export type KillContainer = Schema.Schema.Type<typeof KillContainer>;

/**
 * One contract gate a {@link BuildImage} must run INSIDE the image it just
 * built. `name` is the contract field it came from (`check`, `lintFix`,
 * `coverage`, …); `command` is the shell line the repo's `orca.json` declared
 * for it.
 *
 * The commands are repo-authored (see {@link BuildImage}), so "gates green"
 * means "the repo's own declared gates ran and exited 0 inside the image",
 * never an independent proof.
 *
 * @since 1.0.0
 */
export const BuildImageGate = Schema.Struct({
  name: Schema.String,
  command: Schema.String,
});

/**
 * @since 1.0.0
 */
export type BuildImageGate = Schema.Schema.Type<typeof BuildImageGate>;

/**
 * Command to build a repo's agent-delivered `Dockerfile.orca` into a delta
 * image on the runner, gate it from the inside, and push it (OL-71).
 *
 * The daemon clones `cloneUrl` at the immutable commit `ref`, builds
 * `dockerfile` with the build context set to that clone dir, runs every
 * {@link BuildImageGate} INSIDE the built image with the network off, and
 * pushes `image:tag` ONLY when every gate exited 0. The structured result comes
 * back as {@link CommandAck.detail}.
 *
 * Three fields are deliberately narrow:
 *
 *  - `dockerfile` is the literal `"Dockerfile.orca"` and nothing else — a
 *    caller-supplied path is a traversal vector out of the clone dir;
 *  - `buildArgs` allows EXACTLY one build-arg, `HARNESS_IMAGE` (the base image
 *    the repo's `Dockerfile.orca` must be `FROM`). `Schema.Struct` ignores
 *    excess properties, so a rogue second build-arg is stripped at decode and
 *    can never reach the argv;
 *  - `image`/`tag` are computed server-side from the run's customer; the daemon
 *    pushes exactly the tag it built and never re-tags to another target.
 *
 * There is deliberately **no** `secretEnv`, **no** `env` and **no** `mounts`
 * field: a build carries no secrets, so a secret is unrepresentable on this
 * wire type.
 *
 * @since 1.0.0
 */
export const BuildImage = Schema.TaggedStruct("BuildImage", {
  commandId: Schema.String,
  // Clone source, server-computed. `ref` is an immutable commit sha.
  repo: Schema.String,
  cloneUrl: Schema.String,
  ref: Schema.String,
  // The ONLY dockerfile a build run will ever build. A literal, not a path:
  // a caller-supplied path is a traversal vector out of the clone dir.
  dockerfile: Schema.Literal("Dockerfile.orca"),
  // Push target, computed server-side from the run's customer. The daemon
  // pushes EXACTLY this tag and never re-tags to another target.
  image: Schema.String,
  tag: Schema.String,
  // Allowlist of exactly one build-arg; nothing else is accepted on the wire.
  buildArgs: Schema.Struct({ HARNESS_IMAGE: Schema.String }),
  gates: Schema.Array(BuildImageGate),
  labels: Schema.Record({ key: Schema.String, value: Schema.String }),
});

/**
 * @since 1.0.0
 */
export type BuildImage = Schema.Schema.Type<typeof BuildImage>;

/**
 * A command sent from the orchestrator to the daemon over the runner channel.
 *
 * @since 1.0.0
 */
export const RunnerCommand = Schema.Union(SpawnContainer, KillContainer, BuildImage);

/**
 * @since 1.0.0
 */
export type RunnerCommand = Schema.Schema.Type<typeof RunnerCommand>;

// ============================================================================
// CommandAck
// ============================================================================

/**
 * Outcome of executing a {@link RunnerCommand}.
 *
 * @since 1.0.0
 */
export const CommandOutcome = Schema.Literal("success", "failure");

/**
 * @since 1.0.0
 */
export type CommandOutcome = Schema.Schema.Type<typeof CommandOutcome>;

/**
 * The structured result of a {@link BuildImage}, produced by the DAEMON from
 * docker exit codes — never from a file the agent wrote. It is the only input
 * the orchestrator needs to compute a build run's verdict.
 *
 * `pushed` is true only when every gate exited 0 AND the push succeeded;
 * `digest` is the pushed image's repo digest; `image`/`tag` echo the tag that
 * was actually built and pushed (byte-identical — there is no re-tag path);
 * `gates` carries one entry per gate that RAN, in order, so a failure names the
 * gate and its exit code; `logTail` is the bounded tail of the failing step's
 * output ({@link REPORT_LOG_TAIL_MAX_LINES} × {@link REPORT_LOG_TAIL_MAX_LINE_CHARS}).
 *
 * @since 1.0.0
 */
export const BuildImageDetail = Schema.Struct({
  pushed: Schema.Boolean,
  digest: Schema.optional(Schema.String),
  image: Schema.optional(Schema.String),
  tag: Schema.optional(Schema.String),
  gates: Schema.Array(Schema.Struct({ name: Schema.String, exitCode: Schema.Number })),
  logTail: Schema.optional(Schema.String),
});

/**
 * @since 1.0.0
 */
export type BuildImageDetail = Schema.Schema.Type<typeof BuildImageDetail>;

/**
 * The daemon's acknowledgement of a {@link RunnerCommand}, correlated by
 * `commandId`. `containerId` is the Docker container id affected by the command
 * (present once a spawn succeeds or a kill matches a container); `error` carries
 * the failure detail when `outcome` is `"failure"`.
 *
 * `detail` carries the {@link BuildImageDetail} of a {@link BuildImage} — the
 * verdict-grade result of a build run. It is `Schema.optional`, so every
 * spawn/kill ack (and every ack from a daemon that predates OL-71) decodes
 * unchanged.
 *
 * @since 1.0.0
 */
export const CommandAck = Schema.Struct({
  commandId: Schema.String,
  containerId: Schema.optional(Schema.String),
  outcome: CommandOutcome,
  error: Schema.optional(Schema.String),
  detail: Schema.optional(BuildImageDetail),
});

/**
 * @since 1.0.0
 */
export type CommandAck = Schema.Schema.Type<typeof CommandAck>;

// ============================================================================
// reportContainers + heartbeat
// ============================================================================

/**
 * Semantic agent state a builder container running the herdr terminal runtime
 * reports over its in-container socket CLI (`herdr agent list`). Surfaced per
 * container in a {@link ReportContainersPayload} so the orchestrator can key
 * reconciliation on what each agent is actually doing, not just that the
 * container is up. Absent (see {@link RunningContainer.agentStatus}) when the
 * container is not running herdr (tmux mode) or the state is unreadable.
 *
 * @since 1.0.0
 */
export const AgentStatus = Schema.Literal("working", "idle", "blocked", "done", "unknown");

/**
 * @since 1.0.0
 */
export type AgentStatus = Schema.Schema.Type<typeof AgentStatus>;

/**
 * Hard ceiling on lines a daemon may attach per container (payload bound). A
 * daemon that reports a longer tail must trim to this before reporting; the
 * schema rejects anything larger so an un-bounded tail can never inflate a
 * snapshot.
 *
 * @since 1.0.0
 */
export const REPORT_LOG_TAIL_MAX_LINES = 500;

/**
 * Hard ceiling on characters per attached line — one un-newlined JSON dump must
 * not blow the snapshot. A longer single line makes the whole payload decode
 * fail.
 *
 * @since 1.0.0
 */
export const REPORT_LOG_TAIL_MAX_LINE_CHARS = 2000;

/**
 * A single container the daemon currently has running, as surfaced in a
 * {@link ReportContainersPayload}. `agentStatus` is the herdr {@link AgentStatus}
 * for the container, present only when the container runs herdr and its state
 * was read successfully — absent otherwise (tmux mode or an unreadable socket).
 *
 * `logTail` is the last N lines of the container's `docker logs` (stdout then
 * stderr), attached by daemons that report log tails so the admin logs RPC can
 * serve a remote container's tail without shelling out on the api host. It is
 * bounded on both axes ({@link REPORT_LOG_TAIL_MAX_LINES} ×
 * {@link REPORT_LOG_TAIL_MAX_LINE_CHARS}) and absent on daemons that predate the
 * field, so decode stays backward-compatible.
 *
 * @since 1.0.0
 */
export const RunningContainer = Schema.Struct({
  containerId: Schema.String,
  name: Schema.String,
  image: Schema.String,
  status: Schema.String,
  labels: Schema.Record({ key: Schema.String, value: Schema.String }),
  startedAt: Schema.DateTimeUtc,
  agentStatus: Schema.optional(AgentStatus),
  logTail: Schema.optional(
    Schema.Array(Schema.String.pipe(Schema.maxLength(REPORT_LOG_TAIL_MAX_LINE_CHARS))).pipe(
      Schema.maxItems(REPORT_LOG_TAIL_MAX_LINES),
    ),
  ),
});

/**
 * @since 1.0.0
 */
export type RunningContainer = Schema.Schema.Type<typeof RunningContainer>;

/**
 * Periodic full inventory the daemon pushes so the orchestrator can reconcile
 * its view of running containers against ground truth. `runnerId` identifies the
 * reporting daemon; `reportedAt` is when the snapshot was taken.
 *
 * @since 1.0.0
 */
export const ReportContainersPayload = Schema.Struct({
  runnerId: Schema.String,
  reportedAt: Schema.DateTimeUtc,
  containers: Schema.Array(RunningContainer),
});

/**
 * @since 1.0.0
 */
export type ReportContainersPayload = Schema.Schema.Type<typeof ReportContainersPayload>;

/**
 * Lightweight liveness ping from the daemon. `runningCount` lets the
 * orchestrator notice divergence (and request a fresh
 * {@link ReportContainersPayload}) without waiting for the next full report.
 *
 * @since 1.0.0
 */
export const HeartbeatPayload = Schema.Struct({
  runnerId: Schema.String,
  sentAt: Schema.DateTimeUtc,
  runningCount: Schema.Number,
  /**
   * OL-136: the daemon's monotonic running total of command-stream frames it
   * has CONSUMED. Heartbeat liveness alone cannot distinguish a healthy daemon
   * from one whose consumer died (INF-874) — this counter can: it stops moving
   * while the pings keep flowing.
   *
   * `Schema.optional` (not `NullOr`): a daemon built before the counter shipped
   * OMITS the key entirely and still decodes. A daemon must never send `null`.
   */
  commandsConsumed: Schema.optional(Schema.Int),
});

/**
 * @since 1.0.0
 */
export type HeartbeatPayload = Schema.Schema.Type<typeof HeartbeatPayload>;
